import React from 'react';
import { Outlet } from 'react-router-dom';
import { NotificationsBar } from '@/components/navigation/NotificationsBar';
import { MainNavigation } from '@/components/navigation/MainNavigation';
import { MobileNav } from '@/components/navigation/mobile';
import { Footer } from '@/components/layout/Footer';
import { StatusBanner } from '@/components/layout/StatusBanner';
import { PaymentFailureBanner } from '@/components/layout/PaymentFailureBanner';
import { QuickPeekProvider } from '@/contexts/QuickPeekContext';
import { QuickPeekPanel } from '@/components/ui/QuickPeekPanel';
import { ResearchBreadcrumbProvider } from '@/contexts/ResearchBreadcrumbContext';
import { ResearchBreadcrumbs } from '@/components/ui/ResearchBreadcrumbs';
import { useAuth } from '@/hooks/useAuth';
import { useStateYear } from '@/hooks/useStateYear';
import { cn } from '@/lib/utils';
import { featureFlags } from '@/utils/featureFlags';

/**
 * AppLayout component that persists across route changes
 * Uses React Router's Outlet to render child routes without re-mounting layout components
 * This prevents flicker when navigating between pages
 */
export const AppLayout: React.FC = () => {
  const { user, loading: authLoading, isPaidUser } = useAuth();
  const { states, currentStateId } = useStateYear();

  // Get current state name for navigation
  const currentState = states.find((s) => s.id === currentStateId);
  const currentStateName = currentState?.name || 'Kansas';

  // Prepare user data for navigation components
  const userData = user
    ? {
        email: user.email,
        name: user.user_metadata?.full_name || user.email?.split('@')[0],
        avatar: user.user_metadata?.avatar_url,
      }
    : undefined;

  return (
    <div
      className={cn(
        'min-h-screen w-full max-w-full bg-background overflow-x-hidden',
        'grid grid-rows-[auto_1fr_auto]' // Header, Main, Footer
      )}
    >
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:p-4 focus:bg-background focus:text-foreground focus:underline focus:outline-2 focus:outline-ring"
      >
        Skip to main content
      </a>

      {/* Header - persists across route changes */}
      <header
        className={cn(
          'z-50 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60',
          'w-full max-w-full overflow-x-hidden'
        )}
      >
        <StatusBanner />
        <PaymentFailureBanner />
        {featureFlags.stateSelector && <NotificationsBar />}
        <div
          className={cn(authLoading && 'invisible pointer-events-none')}
          aria-hidden={authLoading}
        >
          <div className="hidden md:block">
            <MainNavigation isAuthenticated={!!user} isPremium={isPaidUser} user={userData} />
          </div>
          <div className="md:hidden">
            <MobileNav
              isAuthenticated={!!user}
              isPremium={isPaidUser}
              currentState={currentStateName}
              availableStates={[
                { name: 'Kansas', code: 'KS', active: true, coming: false },
                { name: 'Missouri', code: 'MO', active: false, coming: true },
                { name: 'Nebraska', code: 'NE', active: false, coming: true },
                { name: 'Iowa', code: 'IA', active: false, coming: true },
              ]}
              user={userData}
            />
          </div>
        </div>
      </header>

      {/* Main Content - Outlet renders child routes without re-mounting layout */}
      <main
        id="main-content"
        tabIndex={-1}
        className="flex-1 min-h-0 min-w-0 w-full max-w-full overflow-x-hidden"
      >
        <ResearchBreadcrumbProvider>
          <ResearchBreadcrumbs />
          <QuickPeekProvider>
            <Outlet />
            <QuickPeekPanel />
          </QuickPeekProvider>
        </ResearchBreadcrumbProvider>
      </main>

      {/* Footer - persists across route changes */}
      <footer
        className={cn('border-t border-border bg-background w-full max-w-full overflow-x-hidden')}
      >
        <Footer />
      </footer>
    </div>
  );
};
